import { useMemo } from 'react';
import { Icon } from '@iconify/react';
import { Input, Textarea } from '@nextui-org/react';
import { LabeledHorizontalRadios } from './claimIntakeUtils';
import { TireSizeFields } from './vehicleNotesTireFields';
import { repairsDocsFieldScrollId } from './repairsDocsTabValidation';
import {
  FLUID_LEVEL_OPTIONS,
  LEAK_DRIP_OPTIONS,
  VEHICLE_MODIFICATION_CLEAR_PATCH,
  formatCapturedTireSize,
  formatOemCapturedTireSize,
  sanitizeLiftAmountInput,
  sanitizePartsLaborWarrantyInput,
  type VehicleNotesFormState,
} from './vehicleNotesUtils';

const YES_NO_OPTIONS = [
  { value: 'no', label: 'No' },
  { value: 'yes', label: 'Yes' },
] as const;

type VehicleNotesSectionProps = Readonly<{
  vehicleNotes: VehicleNotesFormState;
  onVehicleNotesChange: (patch: Partial<VehicleNotesFormState>) => void;
  /** TIRE / TWE: hide Fluid and Codes in the UI only (payload is unchanged). */
  hideFluidAndCodes?: boolean;
  warrantyMonthsError?: string;
  warrantyMilesError?: string;
}>;

export function VehicleNotesSection({
  vehicleNotes,
  onVehicleNotesChange,
  hideFluidAndCodes = false,
  warrantyMonthsError,
  warrantyMilesError,
}: VehicleNotesSectionProps) {
  const capturedInstalledTireSize = useMemo(() => formatCapturedTireSize(vehicleNotes), [vehicleNotes]);
  const capturedOemTireSize = useMemo(() => formatOemCapturedTireSize(vehicleNotes), [vehicleNotes]);
  const showModificationFields = vehicleNotes.isVehicleModified === 'yes';
  const showLiftAmount = showModificationFields && vehicleNotes.hasLiftKit === 'yes';
  const showEngineDescription = showModificationFields && vehicleNotes.hasEngineModifications === 'yes';
  const showLeakFields = vehicleNotes.hasLeaks === 'yes';
  const showCodesField = vehicleNotes.hasWarningLight === 'yes';

  return (
    <div className="bg-content1 rounded-xl border border-default-200 p-4 sm:p-6">
      {hideFluidAndCodes ? null : (
      <>
      <h3 className="text-lg font-semibold text-foreground mb-1">Fluid Section</h3>
      <div className="space-y-4 rounded-xl border border-default-200 p-4">
        <div className="space-y-4">
          <LabeledHorizontalRadios
            required={false}
            label="Coolant"
            value={vehicleNotes.coolantLevel}
            onValueChange={(value) => onVehicleNotesChange({ coolantLevel: value })}
            options={[...FLUID_LEVEL_OPTIONS]}
          />
          <Input
            label="Condition"
            value={vehicleNotes.coolantCondition}
            onValueChange={(value) => onVehicleNotesChange({ coolantCondition: value })}
            variant="bordered"
            classNames={{ inputWrapper: 'border-default-300' }}
          />

          <LabeledHorizontalRadios
            required={false}
            label="Engine Oil"
            value={vehicleNotes.engineOilLevel}
            onValueChange={(value) => onVehicleNotesChange({ engineOilLevel: value })}
            options={[...FLUID_LEVEL_OPTIONS]}
          />
          <Input
            label="Condition"
            value={vehicleNotes.engineOilCondition}
            onValueChange={(value) => onVehicleNotesChange({ engineOilCondition: value })}
            variant="bordered"
            classNames={{ inputWrapper: 'border-default-300' }}
          />

          <LabeledHorizontalRadios
            required={false}
            label="Transmission Fluid"
            value={vehicleNotes.transmissionFluidLevel}
            onValueChange={(value) => onVehicleNotesChange({ transmissionFluidLevel: value })}
            options={[...FLUID_LEVEL_OPTIONS]}
          />
          <Input
            label="Condition"
            value={vehicleNotes.transmissionFluidCondition}
            onValueChange={(value) => onVehicleNotesChange({ transmissionFluidCondition: value })}
            variant="bordered"
            classNames={{ inputWrapper: 'border-default-300' }}
          />

          <LabeledHorizontalRadios
            required={false}
            label="Are there any leaks present?"
            value={vehicleNotes.hasLeaks}
            onValueChange={(value) =>
              onVehicleNotesChange({
                hasLeaks: value,
                ...(value === 'yes' ? {} : { leakLocation: '', leakDripCondition: '' }),
              })
            }
            options={[...YES_NO_OPTIONS]}
          />
          {showLeakFields ? (
            <div className="space-y-3">
              <Input
                label="Where is the leak coming from?"
                value={vehicleNotes.leakLocation}
                onValueChange={(value) => onVehicleNotesChange({ leakLocation: value })}
                variant="bordered"
                classNames={{ inputWrapper: 'border-default-300' }}
              />
              <LabeledHorizontalRadios
                required={false}
                label="Drip Condition"
                value={vehicleNotes.leakDripCondition}
                onValueChange={(value) => onVehicleNotesChange({ leakDripCondition: value })}
                options={[...LEAK_DRIP_OPTIONS]}
              />
            </div>
          ) : null}
        </div>
      </div>

      <div className="space-y-4 rounded-xl border border-default-200 p-4 mt-4">
        <h3 className="text-sm font-semibold text-foreground">Codes Section</h3>
        <LabeledHorizontalRadios
          required={false}
          label="Is there a warning light illuminated?"
          value={vehicleNotes.hasWarningLight}
          onValueChange={(value) =>
            onVehicleNotesChange({
              hasWarningLight: value,
              ...(value === 'yes' ? {} : { codesAndDefinitions: '' }),
            })
          }
          options={[...YES_NO_OPTIONS]}
        />
        {showCodesField ? (
          <Textarea
            label="Codes and Definitions"
            value={vehicleNotes.codesAndDefinitions}
            onValueChange={(value) => onVehicleNotesChange({ codesAndDefinitions: value })}
            variant="bordered"
            minRows={4}
            classNames={{ inputWrapper: 'border-default-300' }}
          />
        ) : null}
      </div>
      </>
      )}

      <div className={`space-y-6 ${hideFluidAndCodes ? '' : 'mt-5'}`}>
        <div
          id="parts-labor-warranty-section"
          className="space-y-3 rounded-xl border border-primary p-4"
        >
          <div>
            <h2 className="text-sm font-semibold text-foreground">
              Parts and labor warranty on recommended repair ?
            </h2>
            <p className="mt-1 text-sm text-default-500">
              Enter a number, or a value such as N/A or Unknown.
            </p>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Input
              id={repairsDocsFieldScrollId('warrantyMonths')}
              label="Months"
              labelPlacement="outside"
              placeholder="e.g. 12, N/A, Unknown"
              isRequired
              value={vehicleNotes.partsLaborWarrantyMonths}
              onValueChange={(value) =>
                onVehicleNotesChange({
                  partsLaborWarrantyMonths: sanitizePartsLaborWarrantyInput(value),
                })
              }
              isInvalid={Boolean(warrantyMonthsError)}
              errorMessage={warrantyMonthsError}
              variant="bordered"
              endContent={<span className="text-sm text-default-400">months</span>}
              classNames={{
                label: 'text-sm font-medium text-foreground',
                inputWrapper: 'border-default-300',
              }}
            />
            <Input
              id={repairsDocsFieldScrollId('warrantyMiles')}
              label="Miles"
              labelPlacement="outside"
              placeholder="e.g. 12000, N/A, Unknown"
              isRequired
              value={vehicleNotes.partsLaborWarrantyMiles}
              onValueChange={(value) =>
                onVehicleNotesChange({
                  partsLaborWarrantyMiles: sanitizePartsLaborWarrantyInput(value),
                })
              }
              isInvalid={Boolean(warrantyMilesError)}
              errorMessage={warrantyMilesError}
              variant="bordered"
              endContent={<span className="text-sm text-default-400">miles</span>}
              classNames={{
                label: 'text-sm font-medium text-foreground',
                inputWrapper: 'border-default-300',
              }}
            />
          </div>
        </div>

        <div
          className="flex gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-900"
          role="note"
        >
          <Icon icon="mdi:alert-outline" className="shrink-0 w-5 h-5" aria-hidden />
          <p>Certain vehicle modifications can impact the contract coverage.</p>
        </div>

        <div className="space-y-4 rounded-xl border border-default-200 p-4">
          <h3 className="text-sm font-semibold text-foreground">Vehicle Modification</h3>
          <LabeledHorizontalRadios
            required={false}
            label="Is the vehicle modified?"
            value={vehicleNotes.isVehicleModified}
            onValueChange={(value) =>
              onVehicleNotesChange({
                isVehicleModified: value,
                ...(value === 'yes' ? {} : VEHICLE_MODIFICATION_CLEAR_PATCH),
              })
            }
            options={[...YES_NO_OPTIONS]}
          />

          {showModificationFields ? (
            <div className="space-y-5 pt-1">
              <TireSizeFields
                title="Installed Tire Size"
                showType
                tireType={vehicleNotes.tireType}
                sectionWidth={vehicleNotes.tireSectionWidthMm}
                aspectRatio={vehicleNotes.tireAspectRatio}
                wheelDiameter={vehicleNotes.tireWheelDiameterIn}
                capturedLabel="Captured tire size"
                capturedValue={capturedInstalledTireSize}
                onChange={onVehicleNotesChange}
              />
              <p className="text-xs text-default-500 -mt-2">
                U.S. P-Metric format: Type · Section Width / Aspect Ratio · R (radial) · Wheel Diameter. Example:{' '}
                <span className="font-medium text-default-700">LT265/70R17</span>
              </p>

              <TireSizeFields
                title="OEM Tire Size"
                sectionWidth={vehicleNotes.oemTireSectionWidthMm}
                aspectRatio={vehicleNotes.oemTireAspectRatio}
                wheelDiameter={vehicleNotes.oemTireWheelDiameterIn}
                capturedLabel="Captured OEM tire size"
                capturedValue={capturedOemTireSize}
                onChange={onVehicleNotesChange}
              />

              <div className="space-y-3">
                <LabeledHorizontalRadios
                  required={false}
                  label="Is the vehicle lifted/lowered?"
                  value={vehicleNotes.hasLiftKit}
                  onValueChange={(value) =>
                    onVehicleNotesChange({
                      hasLiftKit: value,
                      ...(value === 'yes' ? {} : { liftAmountInches: '' }),
                    })
                  }
                  options={[...YES_NO_OPTIONS]}
                />
                {showLiftAmount ? (
                  <Input
                    label="Estimated Inches Lowered or Lifted"
                    value={vehicleNotes.liftAmountInches}
                    onValueChange={(value) =>
                      onVehicleNotesChange({ liftAmountInches: sanitizeLiftAmountInput(value) })
                    }
                    inputMode="decimal"
                    variant="bordered"
                    className="max-w-xs"
                    classNames={{ inputWrapper: 'border-default-300' }}
                  />
                ) : null}
              </div>

              <div className="space-y-3">
                <LabeledHorizontalRadios
                  required={false}
                  label="Are there Engine or drivertrain modifications? For example: Tuner, turbo, super-charged, etc."
                  value={vehicleNotes.hasEngineModifications}
                  onValueChange={(value) =>
                    onVehicleNotesChange({
                      hasEngineModifications: value,
                      ...(value === 'yes' ? {} : { engineModificationDescription: '' }),
                    })
                  }
                  options={[...YES_NO_OPTIONS]}
                />
                {showEngineDescription ? (
                  <Textarea
                    label="List modifications"
                    value={vehicleNotes.engineModificationDescription}
                    onValueChange={(value) =>
                      onVehicleNotesChange({ engineModificationDescription: value })
                    }
                    variant="bordered"
                    minRows={3}
                    classNames={{ inputWrapper: 'border-default-300' }}
                  />
                ) : null}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
